import { isApiName, type ServerMessage } from 'ts-components/PiChat/protocol';
import type { PiChatStart } from 'ts-components/PiChat/usePiChat';
import { KICODE_PROFILES, type KiCodeTarget } from '../profiles';

export interface ResumeRefusal {
  /** The session's stored resume was refused before it ever reached `session.ready`. */
  close: boolean;
}

export const boundAppApiName = (message: ServerMessage) => {
  if (message.type === 'business_app.created') return message.appApiName;
  if (message.type === 'session.ready') return message.context.appApiName;
  return undefined;
};

export const refusesResume = (message: ServerMessage) =>
  message.type === 'error' &&
  (message.code === 'conversation_invalid' ||
    message.code === 'forbidden' ||
    message.code === 'not_found' ||
    (message.code === 'bad_request' &&
      (message.params.reason === 'binding_mismatch' ||
        message.params.reason === 'profile_mismatch')));

export const createKiCodeStart = ({
  url,
  target: initialTarget,
  newConversation,
  resumeId,
}: {
  url: string;
  target: KiCodeTarget;
  newConversation: boolean;
  resumeId: string | null;
}) => {
  let target = initialTarget;
  let conversationId = resumeId;
  let everReady = false;
  // Whether the current connection's start resumed a conversation and is still waiting for `session.ready`.
  let resuming = false;

  /** Called once per socket open, so each start that sends a `conversationId` is a new resume attempt. */
  const getStart = (): PiChatStart => {
    resuming = conversationId !== null;
    const base = {
      url,
      profile: target.profile,
      capabilities: KICODE_PROFILES[target.profile],
      context: target.context,
    };
    if (conversationId) return { ...base, conversationId };
    return newConversation ? { ...base, newConversation: true } : base;
  };

  const bind = (appApiName: string | undefined) => {
    if (
      appApiName === undefined ||
      target.context.appApiName !== undefined ||
      !isApiName(appApiName)
    ) {
      return;
    }
    target = {
      profile: 'business-app',
      context: { targetType: 'business_app', appApiName },
    };
  };

  /** Feeds a frame from the current connection; returns the refusal when it rejects this connection's resume. */
  const handleMessage = (message: ServerMessage): ResumeRefusal | null => {
    bind(boundAppApiName(message));
    if (message.type === 'session.ready') {
      conversationId = message.conversationId;
      everReady = true;
      resuming = false;
      return null;
    }
    if (
      !resuming ||
      message.type !== 'error' ||
      message.clientMessageId !== undefined ||
      !refusesResume(message)
    ) {
      return null;
    }
    resuming = false;
    return { close: resumeId !== null && !everReady };
  };

  return {
    getStart,
    handleMessage,
    conversationId: () => conversationId,
    target: () => target,
  };
};
